import React, { useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import Loader from "components/Loader";
import CustomChannelList from "Messenger/pages/TeamConversation/components/SendbirdCustomComponents/CustomChannelList";
import CustomChannelPreview from "Messenger/pages/TeamConversation/components/SendbirdCustomComponents/CustomChannelPreview";
import CustomConversationHeader from "Messenger/pages/TeamConversation/components/SendbirdCustomComponents/CustomConversationHeader";
import CustomMessageInput from "Messenger/pages/TeamConversation/components/SendbirdCustomComponents/CustomMessageInput";
import CustomMessageItem from "Messenger/pages/TeamConversation/components/SendbirdCustomComponents/CustomMessageItem";
import "../DashboardChat.scss";
import ImageViewer from "Messenger/pages/TeamConversation/components/Modals/ImageViewer";
import useConversation from "Messenger/hooks/conversation/useConversation";
import ChatLoader from "Messenger/chatLoader";
import { GroupChannelListProvider } from "@sendbird/uikit-react/GroupChannelList/context";
import { GroupChannelProvider } from "@sendbird/uikit-react/GroupChannel/context";
import { GroupChannelUI } from "@sendbird/uikit-react/GroupChannel/components/GroupChannelUI";
import NewGroupPopup from "Messenger/pages/TeamConversation/components/Modals/NewGroupPopup";
import CreateGroupPopup from "Messenger/pages/TeamConversation/components/Modals/CreateGroupPopup";
import NewConversationPopup from "Messenger/pages/TeamConversation/components/Modals/NewConversationPopup";
import NewConversation from "Messenger/pages/PatientConversation/component/NewConversation";
import useChannelListHeaderState from "Messenger/hooks/useChannelListHeaderState";
import { chatWindowsName } from "../Chat";
import constants from "../../../../../constants";
import useTeamConversationState from "Messenger/hooks/useTeamConversationState";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";

const DashboardCommonChat = ({
  t,
  currentChannel,
  setCurrentChannel,
  externalTabActive,
  isInternalChat,
  isFromPatientChat,
  selectedOwner,
  appliedOfficeFilters,
  addModalOpen,
  setAddModalOpen,
}) => {
  const { selectedId } = useAccountOwnerCommon();
  /** Match InternalTabContent: staff/office queries need a stable owner id (context can lag behind dashboard props). */
  const ownerForMessenger = useMemo(() => {
    if (selectedOwner?.id != null && selectedOwner.id !== "") {
      return selectedOwner;
    }
    if (selectedId != null && selectedId !== "") {
      return { ...(selectedOwner || {}), id: selectedId };
    }
    return selectedOwner;
  }, [selectedOwner, selectedId]);

  const {
    state: { profile: currentUser },
  } = useTeamConversationState({
    selectedOwner,
  });

  const { state, otherData, updateMethods, otherMethods } = useConversation({
    currentUser,
    currentChannel,
    setCurrentChannel,
    selectedOwner: ownerForMessenger,
    t,
    ...(isInternalChat !== undefined && {
      isInternalChat,
    }),
    ...(externalTabActive !== undefined && {
      isExternalChat: externalTabActive,
    }),
  });
  const {
    profileContactInfo,
    channelListQuery,
    fileMessage,
    fileViewer,
    filteredOffices,
    filteredOfficesForNew,
    selectedOption,
    filteredRoleIds,
    staffDesignationIdsByUserId,
    staffRoleLookupBusy,
  } = state;
  const { isSdkLoading, isUserLoading, sdk, isSdkError } = otherData;

  const {
    setFileMessage,
    setFileViewer,
    setChannelListQuery,
    setFilteredOffices,
    updateChannelList,
    setFilteredOfficesForNew,
    setSelectedOption,
    setFilteredRoleIds,
  } = updateMethods;
  const {
    handleProfileBtn,
    handleProfileBtnClose,
    handleChannelSelect,
    handleBackBtn,
    handleOnBeforeSendUserMessage,
    sendUserMessage,
    sendFileMessage,
    handleOnBeforeSendFileMessage,
    handleRequestStatusFilter,
  } = otherMethods;
  useEffect(() => {
    setChannelListQuery((prev) => {
      return {
        channelListQuery: {
          ...prev.channelListQuery,

          customTypesFilter: isInternalChat
            ? [constants.chat.internal]
            : externalTabActive
              ? [constants.chat.external]
              : isFromPatientChat
                ? [constants.chat.patient.open, constants.chat.patient.close]
                : [],
        },
      };
    });
  }, [isInternalChat, externalTabActive, isFromPatientChat]);
  const stoppingForReexcutingUseEffect = useMemo(() => {
    return {
      allChannelsOfficesForDoctor: [],
    };
  }, []);
  const stateData = useChannelListHeaderState({
    setChannelListQuery,
    setFilteredOffices,
    setCurrentChannel,
    selectedOwner: ownerForMessenger,
    sdk,
    currentUser,
    externalTabActive,
    t,
    updateChannelList,
    isPatient: isFromPatientChat,
    setFilteredOfficesForNew,
    setFilteredRoleIds,
    filteredOffices,
    filteredOfficesForNew,
    allChannelsOfficesForDoctor:
      stoppingForReexcutingUseEffect.allChannelsOfficesForDoctor,
  });
  const {
    state: headerState,
    updateMethods: headerUpdateMethods,
    otherMethods: headerOtherMethods,
  } = stateData;
  const {
    isNewGroupPopupOpen,
    isCreateGroupPopupOpen,
    isNewConversationPopupOpen,
    isPatientConversationPopOpen,
    isNewConversationOfficePopupOpen,
  } = headerState;
  const {
    setIsNewGroupPopupOpen,
    setIsNewConversationPopupOpen,
    setIsPatientConversationPopOpen,
    setIsNewConversationOfficePopupOpen,
  } = headerUpdateMethods;
  useEffect(() => {
    if (addModalOpen) {
      switch (addModalOpen) {
        case chatWindowsName.patient:
          setIsPatientConversationPopOpen(true);
          break;
        case chatWindowsName.internal:
          setIsNewGroupPopupOpen(true);
          break;
        case chatWindowsName.external:
          setIsNewConversationPopupOpen(true);
          break;

        default:
          break;
      }
    }
  }, [addModalOpen]);
  useEffect(() => {
    const check =
      isNewGroupPopupOpen ||
      isCreateGroupPopupOpen ||
      isNewConversationPopupOpen ||
      isPatientConversationPopOpen ||
      isNewConversationOfficePopupOpen;
    if (addModalOpen && !check) {
      setAddModalOpen("");
    }
  }, [
    isNewGroupPopupOpen,
    isCreateGroupPopupOpen,
    isNewConversationPopupOpen,
    isPatientConversationPopOpen,
    isNewConversationOfficePopupOpen,
  ]);
  const { selectedOfficesLabel, selectedRolesLabel, resetOfficePagination } =
    headerOtherMethods;
  const isMobile = window.innerWidth < 767;
  const minHeight = isMobile
    ? "350px"
    : currentUser.isAccountOwner
      ? "373px"
      : "350px";
  const noChatDetail = useMemo(() => {
    if (isInternalChat) {
      return t("messenger.pleaseInternalSelectConversationToStart");
    }
    if (externalTabActive) {
      return t("messenger.pleaseExternalSelectConversationToStart");
    }
    return t("messenger.pleasePatientSelectConversationToStart");
  }, [isInternalChat, externalTabActive, isFromPatientChat]);

  return (
    <div
      className={`miraxis-messenger  ${
        isFromPatientChat ? "miraxis_new1" : "miraxis_new"
      } `}
    >
      {!currentChannel ? (
        <>
          {(isSdkLoading || isUserLoading) && <Loader />}
          {/* Chat list */}
          <GroupChannelListProvider
            className={!currentChannel ? "" : "hide-channel-list"}
            disableAutoSelect={true}
            onChannelSelect={handleChannelSelect}
            channelListQueryParams={channelListQuery.channelListQuery}
          >
            <CustomChannelList
              isSdkError={isSdkError}
              currentUser={currentUser}
              externalTabActive={externalTabActive}
              isFromDashBoard={true}
              selectedOwner={ownerForMessenger}
              {...(isFromPatientChat !== undefined && {
                filteredOffices: appliedOfficeFilters,
                isFromPatient: isFromPatientChat,
              })}
              {...(isInternalChat !== undefined && {
                filteredOffices: appliedOfficeFilters,
                internalTabActive: isInternalChat,
                filteredRoleIds,
                staffDesignationIdsByUserId,
                staffRoleLookupBusy,
              })}
              renderChannelPreview={(props) => (
                <CustomChannelPreview
                  {...props}
                  key={props.channel.url}
                  currentUser={currentUser}
                  currentChannelUrl={currentChannel?.url}
                  setCurrentChannel={setCurrentChannel}
                  externalTabActive={externalTabActive}
                  handleProfileBtnClose={handleProfileBtnClose}
                  isFromDashBoard={true}
                  sdk={sdk}
                />
              )}
            />
          </GroupChannelListProvider>
        </>
      ) : (
        <div
          style={
            isFromPatientChat
              ? { "--wrapper-min-height": "436px" }
              : isInternalChat
                ? { "--wrapper-min-height": "373px" }
                : {
                    "--wrapper-min-height": minHeight,
                  }
          }
          className={`conversation-wrapper 
                ${!currentChannel ? "" : "show-conversation"}
                ${!profileContactInfo ? "" : "contact-info-active"}`}
        >
          <GroupChannelProvider
            channelUrl={currentChannel?.url}
            onBeforeSendFileMessage={handleOnBeforeSendFileMessage}
            onBeforeSendUserMessage={handleOnBeforeSendUserMessage}
          >
            <GroupChannelUI
              renderTypingIndicator={() => <></>}
              renderPlaceholderInvalid={() => (
                <div className="empty-chat-box">{noChatDetail} </div>
              )}
              renderPlaceholderLoader={() => <ChatLoader />}
              renderPlaceholderEmpty={() => <></>}
              renderChannelHeader={() => (
                <header className="dashboard_chat_header">
                  <CustomConversationHeader
                    currentUser={currentUser}
                    channel={currentChannel}
                    handleProfileBtn={handleProfileBtn}
                    handleBackBtn={handleBackBtn}
                    isFromDashBoard={true}
                  />
                </header>
              )}
              renderMessage={(props) => (
                <CustomMessageItem
                  {...props}
                  t={t}
                  currentUser={currentUser}
                  setFileMessage={setFileMessage}
                  setFileViewer={setFileViewer}
                  currentChannel={currentChannel}
                />
              )}
              renderMessageInput={() => (
                <CustomMessageInput
                  t={t}
                  sendUserMessage={sendUserMessage}
                  sendFileMessage={sendFileMessage}
                  currentChannel={currentChannel}
                  sdk={sdk}
                />
              )}
            />
          </GroupChannelProvider>
          {fileViewer && fileMessage && (
            <ImageViewer
              imageUrl={fileMessage.url}
              isOpen={fileViewer}
              closeImageViewerModal={() => setFileViewer(false)}
            />
          )}
        </div>
      )}
      {/* This show the first step of group/one-2-one creation to select the users */}
      {isNewGroupPopupOpen && (
        <NewGroupPopup
          stateData={stateData}
          selectedOfficesLabel={selectedOfficesLabel}
          selectedRolesLabel={selectedRolesLabel}
          isNewConversationOfficePopupOpen={isNewConversationOfficePopupOpen}
          setIsNewConversationOfficePopupOpen={
            setIsNewConversationOfficePopupOpen
          }
        />
      )}
      {/* This shows the 2nd Step of adding image an name to a group */}
      {isCreateGroupPopupOpen && (
        <CreateGroupPopup
          stateData={stateData}
          isPatientConversation={isFromPatientChat}
        />
      )}
      {/* This is the first and only step of chat creation for external chat */}
      {isNewConversationPopupOpen && (
        <NewConversationPopup stateData={stateData} />
      )}
      {isPatientConversationPopOpen && (
        <NewConversation
          selectedOfficesLabel={selectedOfficesLabel}
          resetOfficePagination={resetOfficePagination}
          isNewConversationOfficePopupOpen={isNewConversationOfficePopupOpen}
          setIsNewConversationOfficePopupOpen={
            setIsNewConversationOfficePopupOpen
          }
          stateData={stateData}
          setSelectedOption={setSelectedOption}
          filteredOfficesForNew={filteredOfficesForNew}
          setFilteredOfficesForNew={setFilteredOfficesForNew}
          handleRequestStatusFilter={handleRequestStatusFilter}
          selectedOption={selectedOption}
        />
      )}
    </div>
  );
};

export default withTranslation()(DashboardCommonChat);
